import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { NotificationPanelService } from '@core/services';
import { TranslateDecoratorService } from '@core/translate-decorator.service';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { CrudOperation } from '@shared/models';
import { AppNotificationService, HeaderService } from '@shared/services';
import { SecurityResponseAuditable } from 'src/app/web-api-client';
import { UpsertSecurityState } from '../../models/upsert-security-state';

@Component({
  standalone: false,
  selector: 'gstar-upsert-specific-advice',
  templateUrl: './upsert-specific-advice.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertSpecificAdviceComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly headerService = inject(HeaderService);
  private readonly translateDecorator = inject(TranslateDecoratorService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly notificationPanelService = inject(NotificationPanelService);

  @Input() transactionType!: string;
  @Input() securityId!: string;

  state!: UpsertSecurityState;
  securityTitle!: string;
  tooltipMessage!: string;
  translate?: TranslateDecoratorTemplate;

  isUpsertFormSecurityEnabled: Signal<boolean> = computed(
    () => !!this.headerService.selectedClient()
  );

  constructor() {
    const securityTitleTemplate: JSONObject = {
      securityTitle: [
        'requestSpecificAdviceTitle',
        'generalRatesTitle',
        'editRequestSpecificAdviceTitle',
        'editGeneralRatesTitle',
      ],
    };

    const transactionTypeTemplate = {
      transactionType: [
        'requestSpecificAdvice',
        'generalRates',
        'requestSpecificAdvice',
        'generalRates',
      ],
    };

    this.translate = this.translateDecorator
      .path(['upsertSecurity'])
      .assignTemplates([securityTitleTemplate, transactionTypeTemplate])
      .decorator as TranslateDecoratorTemplate;
  }

  ngOnInit(): void {
    if (!this.transactionType) {
      this.transactionType = TransactionType.SpecificAdvice.toString();
    }

    this.state = new UpsertSecurityState(this.transactionType, this.securityId);
    if (this.translate) {
      this.securityTitle = this.translate('securityTitle', this.state.mode);
      this.tooltipMessage = this.translate('transactionType', this.state.mode);
    }

    if (this.state.isUpdateGeneralRates || this.state.isUpdateSpecificAdvice) {
      this.headerService.disable();
    }
  }

  ngOnDestroy(): void {
    this.headerService.enable();
    if (!this.headerService.isClientManuallySelected()) {
      this.headerService.clearSelection();
    }
  }

  handleSuccessAdded(securityResponseAudit: SecurityResponseAuditable): void {
    this.notificationPanelService.reloadPendingApprovalCounter();
    this.appNotificationService.crudNotify(
      CrudOperation.Create,
      this.tooltipMessage,
      true
    );
    this.router.navigate([
      '/portfolio',
      securityResponseAudit.securityId,
      'details',
    ]);
  }

  handleSuccessfulUpdate(): void {
    this.notificationPanelService.reloadPendingApprovalCounter();
    this.appNotificationService.crudNotify(
      CrudOperation.Update,
      this.tooltipMessage,
      true
    );
    this.router.navigate(['/portfolio', this.securityId, 'details']);
  }
}
